import { Box, Card, Flex, Text } from '@mantine/core';
import { GetMonthlyEnergyStatsResponse } from 'api/actions/get-monthly-energy-stats/get-monthly-energy-stats-response';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { yGridConfig, barDatasetBase } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface HourlyHistogramCardProps {
  /** Monthly energy statistics including 15-minute interval histogram */
  monthlyStats: GetMonthlyEnergyStatsResponse;
}

/**
 * Displays a bar chart of average electricity consumption per 15-minute interval of the day.
 * Data is aggregated over the whole month to reveal typical usage patterns.
 *
 * @prop {GetMonthlyEnergyStatsResponse} monthlyStats - Monthly stats with hourlyHistogram array.
 */
export default function HourlyHistogramCard({ monthlyStats }: HourlyHistogramCardProps) {
  const { _t } = useLang();
  const { hourlyHistogram, month } = monthlyStats;

  const chartData = useMemo(
    () => ({
      labels: hourlyHistogram.map((interval) => interval.label),
      datasets: [
        {
          ...barDatasetBase,
          label: _t('Average consumption (Wh)'),
          data: hourlyHistogram.map((interval) => interval.averageConsumption || 0),
          backgroundColor: '#551FFF',
          hoverBackgroundColor: '#4016CC',
        },
      ],
    }),
    [_t, hourlyHistogram]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: { color: theme.colors?.gray?.[7] },
          grid: { display: false },
        },
        y: {
          ticks: {
            color: theme.colors?.gray?.[7],
            callback: (v: number | string) => `${Number(v).toFixed(0)} Wh`,
          },
          grid: yGridConfig,
          beginAtZero: true,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => `${_t('Average consumption')}: ${ctx.parsed.y.toFixed(2)} Wh`,
          },
        },
      },
    }),
    [_t]
  );

  const totalAvgDaily = hourlyHistogram.reduce((sum, interval) => sum + (interval.averageConsumption || 0), 0) / 1000;
  const daysInMonth = Math.floor((hourlyHistogram[0]?.intervalCount || 0) / 4);

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Average consumption by 15-minute intervals')} - {month}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#F8F9FA', borderRadius: 8, border: '1px solid #E9ECEF' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total average daily consumption')}:
          </Text>
          <Text size="xl" fw={700} c="#551FFF">
            {totalAvgDaily.toFixed(2)} kWh
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          {_t('Average from')} {daysInMonth} {_t('days in the month')}
        </Text>
      </Box>
    </Card>
  );
}
